import { Box, Button, Grid2 as Grid, Typography, useMediaQuery } from '@mui/material';
import {
  CHAMPS_API,
  CLANS_CHAMP_API,
  CLANS_DETAILS_API,
  CLANS_LIST_API,
  USER_BY_IDS_API,
  USER_COUNTER_API
} from '../../api/endpoint';
import React, { useEffect, useRef, useState } from 'react';
import { UserCounters, UserWithCoins } from '../../types';
import explore_more from '../../assets/ExploreNow.png';
import ChampIcons from './champ-icons';
import IconsContainer from '../../svg/icons-container';
import Leaderboard from './leaderboard';
import Loader from '../../components/loader';
import MobileIconsContainer from '../../svg/mobile-icons-container';
import TimelineButtons from './timeline-buttons';
import TopChamps from './top-champs';
import axiosInstance from '../../api/axiosConfig';
import counterInstance from '../../api/counterConfig';
import createAxiosInstance from '../../api/axiosConfig';
import desktop_champ_bg from '../../assets/desktop_champ_bg.svg';
import mobile_champ_bg from '../../assets/mobile_champ_bg.svg';

const parseCounter = (counter: { N: string } | undefined): number => parseInt(counter?.N || '0', 10);

const Champs = () => {
  const [timeline, setTimeline] = useState('all_time');
  const [champType, setChampType] = useState('coins');
  const [userDetails, setUserDetails] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageToken, setPageToken] = useState<string | null>(null);
  const [stopFetching, setStopFetching] = useState(false);
  const isFetching = useRef(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const items = ['all_time', 'current_month', 'current_week'];
  const [leaderboardLength, setLeaderboardLength] = useState(20);

  const fetchData = async (type: string, period: string, currentPageToken: string | null = null) => {
    if (isFetching.current || stopFetching) return;

    isFetching.current = true;
    setLoading(true);
    setError(null);

    try {
      const response =
        type === 'clans' ? await fetchClans(currentPageToken) : await fetchChamps(type, period, currentPageToken);

      const newPageToken = response?.page || null;
      if (!newPageToken) setStopFetching(true);

      setPageToken(newPageToken);
    } catch (error) {
      setError('An error occurred while fetching data');
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  const fetchChamps = async (type: string, period: string, currentPageToken: string | null = null) => {
    try {
      const axiosInstance = createAxiosInstance(true);
      const response = await axiosInstance.get(`${CHAMPS_API}/${type}/${period}.json`, {
        params: {
          count: 20,
          page: currentPageToken
        }
      });

      const champData = response.data?.data || [];
      setUserDetails((prevDetails: any) => [...prevDetails, ...champData]);

      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch champions');
    }
  };

  const fetchClans = async (currentPageToken: string | null = null) => {
    try {
      const axiosInstance = createAxiosInstance(true);
      const response = await axiosInstance.get(CLANS_CHAMP_API, {});
      const clanData = response.data?.data || [];
      const allUserDetails: any[] = [];
      setUserDetails((prevDetails: any) => [...prevDetails, ...clanData]);
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch clans');
    }
  };

  const getRandomAvatarPath = () =>
    `${process.env.REACT_APP_DEFAULT_AVATAR}/APP/UserAvatars/Avatar${Math.floor(Math.random() * 8) + 1}.png`;

  const handleLoadMore = () => {
    setLeaderboardLength((prev) => prev + 20);
  };

  useEffect(() => {
    fetchData(champType, timeline);
  }, [champType, timeline]);

  if (loading && !userDetails.length) return <Loader />;

  if (error)
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );

  const topChamps = userDetails.slice(0, 3);
  const leaderboard = userDetails.slice(3, leaderboardLength);

  const handleTimelineChange = (newTimeline: string) => {
    if (newTimeline !== timeline) {
      resetData();
      setTimeline(newTimeline);
    }
  };

  const handleChampTypeChange = (newChampType: string) => {
    if (newChampType !== champType) {
      resetData();
      setChampType(newChampType);
    }
  };

  const resetData = () => {
    setUserDetails([]);
    setPageToken(null);
    setStopFetching(false);
  };

  return (
    <Grid
      container
      spacing={2}
      direction="column"
      alignItems="center"
      sx={{
        mt: 2,
        color: '#1F1D3A',
        pl: isMobile ? 2 : 0,
        pr: isMobile ? 2 : 0,
        pb: isMobile ? 0 : '30px'
      }}>
      <TimelineButtons page={'champs'} items={items} timeline={timeline} handleTimelineChange={handleTimelineChange} />
      {isMobile ? (
        <ChampIcons isMobile={isMobile} champType={champType} handleChampTypeChange={handleChampTypeChange} />
      ) : (
        <ChampIcons isMobile={isMobile} champType={champType} handleChampTypeChange={handleChampTypeChange} />
      )}
      {loading && pageToken ? (
        <Loader />
      ) : (
        <>
          <TopChamps topChamps={topChamps} isMobile={isMobile} type={champType} />
          <Leaderboard leaderboard={leaderboard} isMobile={isMobile} type={champType} />
        </>
      )}

      {/* {loading && pageToken && <Loader />} */}
      {!loading && (
        <Button
          onClick={handleLoadMore}
          size="large"
          sx={{
            mt: 2
          }}>
          <img src={explore_more} alt="Explore More" style={{ width: isMobile ? '30vw' : '20vw' }} />
        </Button>
      )}
    </Grid>
  );
};

export default Champs;
